<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import OperationalCatalogAPI from 'custom/api/operationalCatalog';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  resourceKind: {
    type: String,
    required: true,
  },
  resourceId: {
    type: Number,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['ready', 'update:modelValue']);

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const enabled = ref(false);

const scope = computed(() => props.modelValue);

const updateScope = changes => {
  emit('update:modelValue', { ...scope.value, ...changes });
};

const useFullAccount = () => {
  updateScope({ all: true, inbox_ids: [], team_ids: [] });
};

const useSelectedContexts = () => {
  updateScope({ all: false });
};

const isChecked = (key, id) => scope.value[key].includes(id);

const toggleContext = (key, id, checked) => {
  const ids = new Set(scope.value[key]);
  if (checked) ids.add(id);
  else ids.delete(id);
  updateScope({ [key]: [...ids] });
};

const loadScope = async () => {
  try {
    const { data } = props.resourceId
      ? await OperationalCatalogAPI.getScope({
          resourceKind: props.resourceKind,
          resourceId: props.resourceId,
        })
      : await OperationalCatalogAPI.resolve();

    enabled.value = data.enabled;
    if (data.operational_scope) {
      emit('update:modelValue', data.operational_scope);
    }
  } catch (error) {
    enabled.value = false;
    Sentry.captureException(error);
  }
};

onMounted(async () => {
  await Promise.allSettled([
    store.dispatch('inboxes/get'),
    store.dispatch('teams/get'),
    loadScope(),
  ]);
  emit('ready');
});
</script>

<template>
  <div>
    <section v-if="enabled" class="flex flex-col gap-3 py-3">
      <div>
        <h4 class="text-heading-3 text-n-slate-12">
          {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.TITLE') }}
        </h4>
        <p class="text-body-small text-n-slate-11">
          {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.DESCRIPTION') }}
        </p>
      </div>

      <label class="flex items-start gap-2 cursor-pointer">
        <input
          type="radio"
          :checked="scope.all"
          :disabled="disabled"
          class="mt-1"
          @change="useFullAccount"
        />
        <span class="text-body-main text-n-slate-12">
          {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.ALL_ACCOUNT') }}
        </span>
      </label>

      <label class="flex items-start gap-2 cursor-pointer">
        <input
          type="radio"
          :checked="!scope.all"
          :disabled="disabled"
          class="mt-1"
          @change="useSelectedContexts"
        />
        <span class="text-body-main text-n-slate-12">
          {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.SELECTED_CONTEXTS') }}
        </span>
      </label>

      <div v-if="!scope.all" class="grid grid-cols-1 gap-4 pl-6 sm:grid-cols-2">
        <div class="flex flex-col gap-2">
          <span class="text-label-1 text-n-slate-11">
            {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.INBOXES') }}
          </span>
          <label
            v-for="inbox in inboxes"
            :key="inbox.id"
            class="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="isChecked('inbox_ids', inbox.id)"
              :disabled="disabled"
              @change="
                toggleContext('inbox_ids', inbox.id, $event.target.checked)
              "
            />
            <span class="text-body-small text-n-slate-12">{{
              inbox.name
            }}</span>
          </label>
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-label-1 text-n-slate-11">
            {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.TEAMS') }}
          </span>
          <label
            v-for="team in teams"
            :key="team.id"
            class="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="isChecked('team_ids', team.id)"
              :disabled="disabled"
              @change="
                toggleContext('team_ids', team.id, $event.target.checked)
              "
            />
            <span class="text-body-small text-n-slate-12">{{ team.name }}</span>
          </label>
        </div>
      </div>

      <p
        v-if="!scope.all && !scope.inbox_ids.length && !scope.team_ids.length"
        class="text-body-small text-n-ruby-11"
      >
        {{ t('CANNED_MGMT.OPERATIONAL_SCOPE.REQUIRED') }}
      </p>
    </section>
  </div>
</template>
